import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Scissors, Package, DollarSign, Trash2 } from 'lucide-react';

/**
 * Read-only breakdown of a barber's commissions (services + products).
 * Props:
 *  - data: response payload from GET /api/barbers/{id}/commissions (or null when closed)
 *  - onClose(): parent state setter to null
 *  - onDeleteCommission(commissionId, barberId): handler to delete a single commission row
 */
export default function BarberCommissionsDialog({ data, onClose, onDeleteCommission }) {
  return (
    <Dialog open={!!data} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white">
            Comisiones de {data?.barber_name}
          </DialogTitle>
        </DialogHeader>
        {data && (
          <div className="space-y-4 mt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase">Servicios</p>
                <p className="text-xl text-white font-semibold">{data.total_services || 0}</p>
              </div>
              <div className="p-3 bg-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase">Com. Serv.</p>
                <p className="text-xl text-gold font-semibold">{data.commission_percentage}%</p>
              </div>
              <div className="p-3 bg-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase">Productos</p>
                <p className="text-xl text-white font-semibold">{data.total_products || 0}</p>
              </div>
              <div className="p-3 bg-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase">Com. Prod.</p>
                <p className="text-xl text-blue-400 font-semibold">{data.product_commission_percentage || 10}%</p>
              </div>
            </div>

            {/* Total Commission */}
            <div className="p-4 bg-gold/10 border border-gold/30 rounded-sm">
              <p className="text-xs text-gold uppercase">Total Comisiones</p>
              <p className="text-3xl text-gold font-heading">${(data.total_commission || 0).toFixed(2)}</p>
              <div className="flex gap-4 mt-2 text-sm">
                <span className="text-zinc-400">
                  Servicios: <span className="text-gold">${(data.total_services_commission || 0).toFixed(2)}</span>
                </span>
                <span className="text-zinc-400">
                  Productos: <span className="text-blue-400">${(data.total_products_commission || 0).toFixed(2)}</span>
                </span>
              </div>
            </div>

            {/* Services Breakdown */}
            {data.services_breakdown?.length > 0 && (
              <CommissionList
                title="Comisiones por Servicios"
                icon={<Scissors className="w-3 h-3" />}
                items={data.services_breakdown}
                totalLabel={data.total_services}
                bgClass="bg-zinc-800/50"
                amountColor="text-gold"
                onDelete={(id) => onDeleteCommission(id, data.barber_id)}
              />
            )}

            {/* Products Breakdown */}
            {data.products_breakdown?.length > 0 && (
              <CommissionList
                title="Comisiones por Productos"
                icon={<Package className="w-3 h-3" />}
                items={data.products_breakdown}
                totalLabel={data.total_products}
                bgClass="bg-blue-500/10 border border-blue-500/20"
                amountColor="text-blue-400"
                onDelete={(id) => onDeleteCommission(id, data.barber_id)}
              />
            )}

            {(!data.services_breakdown?.length && !data.products_breakdown?.length) && (
              <div className="text-center py-8 text-zinc-500">
                <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No hay comisiones registradas</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CommissionList({ title, icon, items, totalLabel, bgClass, amountColor, onDelete }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2 flex items-center gap-2">
        {icon}
        {title} ({totalLabel})
      </p>
      <ScrollArea className="h-[150px]">
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={`${title}-${i}`} className={`flex items-center justify-between p-2 rounded-sm group ${bgClass}`}>
              <div>
                <p className="text-sm text-white">{item.item_name}</p>
                <p className="text-xs text-zinc-500">{item.date}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm text-zinc-400">${(item.price || 0).toFixed(2)}</p>
                  <p className={`text-sm ${amountColor}`}>+${(item.commission || 0).toFixed(2)}</p>
                </div>
                {item.id && !item.paid && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(item.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-400 hover:bg-red-500/10 p-1 h-auto"
                    title="Eliminar comisión"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
